import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { CardContent, Grid, Typography, useTheme, styled } from "@mui/material";
import { BookingErrorType, IPositioningFlight, IUser } from "@vmosolutions/oc-interfaces";
import { useEffect } from "react";
import { CrewMemberChips } from "src/components/CrewSchedule/CrewMemberChips";
import { formatDateString } from "src/components/DateDisplay";
import { PosFlightChip } from "src/components/Pairings/PosFlightChip";
//
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { BookingErrorStatusCell } from "src/components/FilterSearchTable/CustomCellComponents/ErrorStatusCell";
import { storeAppUsers } from "src/redux/actions/Structure.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IPairingEvent {
  _id: any;
  departure: number;
  arrival: number;
  positioningFlight_id: string;
  airlineIATA?: string;
  flightNumber: number;
  origin: string;
  originTimezoneId: string;
  destination: string;
  destinationTimezoneId: string;
  crew_id: string;
  associatedPairing_id: string;
  associatedPairingShortId: string;
  associatedBookingLocators: [];
  errorType: BookingErrorType;
}

interface IPairingProps {
  pairingInfo: IPairingEvent[];
  isSelected: boolean;
  displayAsUTC: boolean;
}

const OCPairingCard = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "10px 5px",
  "&.MuiCardContent-root:last-child": { paddingBottom: "10px" },
  "&.edit": {
    padding: "4px 0px",
    "&.MuiCardContent-root:last-child": { paddingBottom: "4px" },
  },
  "&.selected": {},
  "& .booking_icon": {
    width: 40,
    margin: "2px 5px 2px 0px",
  },
  "& .section": {
    display: "flex",
    margin: "0px 5px",
    "&.details": {
      width: 150,
      alignItems: "flex-start",
      "& .locator": {
        fontSize: 14,
        textTransform: "uppercase",
        color: theme.palette.primary.contrastText,
      },
      "& .agency": {
        fontSize: 12,
        lineHeight: "14px",
        overflowWrap: "normal",
        whiteSpace: "normal",
      },
    },
    "&.flights": {
      "& .positioning_flight": {
        alignItems: "center",
        "& .booking_chip .posflightchip": {
          padding: "0px 5px", //skinny chips
        },
        "& .departure_arrival": {
          minWidth: 230,
          display: "flex",
          margin: "0px 10px",
          fontSize: 14,
          lineHeight: "18px",
          "& svg": { height: 16 },
        },
      },
      "& .warnings": {
        margin: "2px 0px",
        color: theme.palette.error.main,
        lineHeight: "12px",
        maxWidth: 350,
        "& svg": {
          width: 14,
          height: 14,
          verticalAlign: "text-bottom",
          marginRight: 5,
        },
      },
    },
  },
  "& .card_actions": {
    marginTop: 10,
    "& button.edit_booking, button.remove_booking": {
      padding: "0px 8px",
      margin: 5,
      width: "auto",
      fontSize: 12,
      borderRadius: 2,
    },
  },
}));

/**
 * Summary of the booking errors from the pairing's point of view.
 */
export const PairingCard = (props: IPairingProps): JSX.Element => {
  const { pairingInfo, displayAsUTC } = props;

  const theme = useTheme();
  const dispatch = useDispatch();

  const appUsers = useSelector((state: IRootState) => state.structures.appUsers);

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (response) dispatch(storeAppUsers(response));
    };
    if (appUsers?.length === 0) getUsersAction();
  }, [dispatch, appUsers]);

  return (
    <OCPairingCard>
      <Grid container direction="row" alignItems="flex-start">
        <Grid item className="section flights">
          <Grid container direction="column">
            {pairingInfo?.length > 0 ? (
              pairingInfo.map((flight, index) => {
                const { _id, origin, destination, departure, arrival, airlineIATA, flightNumber, errorType, crew_id } =
                  flight;
                const user = appUsers.find((user) => user._id === crew_id);
                const crewInfo = user
                  ? {
                      _id: user._id,
                      firstName: user.firstName,
                      lastName: user.lastName,
                      employeeId: user.employeeId,
                    }
                  : null;
                const posFlight: IPositioningFlight = {
                  _id,
                  origin,
                  destination,
                  outTime: departure,
                  inTime: arrival,
                  airlineIATA,
                  flightNumber,
                  bookings: [],
                  isGround: false,
                  createdBy: null,
                  createdAt: null,
                };

                return (
                  <Grid container key={index} direction="column" style={{ margin: "5px" }}>
                    <Grid item className="positioning_flight">
                      <div style={{ display: "flex" }}>
                        <BookingErrorStatusCell value={errorType} />
                        <div className="booking_chip">
                          <PosFlightChip data={posFlight} />
                        </div>
                        {!!user && <CrewMemberChips crewInfo={[crewInfo]} hideHeader={true} />}
                      </div>
                      <div style={{ display: "flex" }}>
                        <Typography
                          variant="body1"
                          color={displayAsUTC ? "inherit" : "secondary"}
                          className="departure_arrival"
                        >
                          {formatDateString(
                            flight.departure,
                            displayAsUTC ? "Etc/UTC" : flight?.originTimezoneId,
                            true
                          )}
                          <ArrowRightIcon />
                          {formatDateString(
                            flight.arrival,
                            displayAsUTC ? "Etc/UTC" : flight?.destinationTimezoneId,
                            true
                          )}
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                );
              })
            ) : (
              <Typography variant="body1" className="departure_arrival">
                None Assigned
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </OCPairingCard>
  );
};
